/**
 * VoiceDictateButton — Tiny mic button that appends browser-native
 * SpeechRecognition transcript into a text field.
 *
 * Usage:
 *   <VoiceDictateButton
 *     onTranscript={(text) => setNotesDraft(prev => (prev ? prev + " " : "") + text)}
 *     lang="en-US"
 *   />
 *
 * - Uses window.SpeechRecognition / webkitSpeechRecognition (free, no API
 *   key needed). Works on Chrome / Chrome mobile / Safari 14.5+.
 * - Auto-appends the transcript rather than replacing existing text, so
 *   techs can layer dictation on top of typed notes.
 * - Renders a small disabled hint if the browser doesn't support SR.
 */
import { useEffect, useRef, useState } from "react";
import { Microphone, StopCircle } from "@phosphor-icons/react";

// eslint-disable-next-line no-undef
const SR = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);

export default function VoiceDictateButton({
  onTranscript,
  lang = "en-US",
  size = "md",         // "sm" | "md"
  testId = "voice-dictate",
  disabled = false,
}) {
  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState("");
  const recRef = useRef(null);

  useEffect(() => () => { try { recRef.current?.stop(); } catch {} }, []);

  if (!SR) {
    return (
      <button type="button" disabled title="Voice dictation not supported in this browser"
        className="h-9 w-9 rounded-full border-2 border-slate-200 bg-slate-100 text-slate-300 flex items-center justify-center cursor-not-allowed"
        data-testid={`${testId}-unsupported`}>
        <Microphone size={14}/>
      </button>
    );
  }

  const start = () => {
    setInterim("");
    const rec = new SR();
    rec.lang = lang;
    rec.interimResults = true;
    rec.continuous = true;
    rec.maxAlternatives = 1;

    let finalized = "";
    rec.onresult = (e) => {
      let intr = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const r = e.results[i];
        if (r.isFinal) finalized += r[0].transcript + " ";
        else intr += r[0].transcript;
      }
      setInterim(intr);
      if (finalized.trim()) {
        onTranscript?.(finalized.trim());
        finalized = "";
      }
    };
    rec.onerror = () => { setListening(false); setInterim(""); };
    rec.onend = () => { setListening(false); setInterim(""); };
    recRef.current = rec;
    setListening(true);
    try { rec.start(); }
    catch { setListening(false); }
  };

  const stop = () => { try { recRef.current?.stop(); } catch {} };

  const sz = size === "sm"
    ? { h: "h-8 w-8", icon: 12 }
    : { h: "h-9 w-9", icon: 14 };

  return (
    <div className="relative inline-flex flex-col items-end" data-testid={testId}>
      {!listening ? (
        <button type="button" onClick={start} disabled={disabled}
          title="Dictate a voice note"
          className={`${sz.h} rounded-full bg-slate-800 hover:bg-black active:scale-95 text-white flex items-center justify-center shadow-md transition-transform disabled:opacity-40`}
          data-testid={`${testId}-start`}>
          <Microphone size={sz.icon} weight="fill"/>
        </button>
      ) : (
        <button type="button" onClick={stop}
          title="Stop recording"
          className={`${sz.h} rounded-full bg-red-600 text-white flex items-center justify-center shadow-lg animate-pulse ring-4 ring-red-200`}
          data-testid={`${testId}-stop`}>
          <StopCircle size={sz.icon} weight="fill"/>
        </button>
      )}
      {listening && interim && (
        <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 max-w-[240px] px-2 py-1 rounded-md bg-red-50 border border-red-200 text-[10px] text-red-900 italic truncate">
          {interim}
        </div>
      )}
    </div>
  );
}
